import { inject } from '@angular/core';
import { SCOPED_ACCESS_GATE } from '@mb/auth/client';
import { TableStateService, injectMutationActions } from '@mb/components';
import { clientLogger } from '@mb/common/client';
import type { IPerson } from '../../shared';
import { ContactsApiService } from '../services/contacts-api.service';
import { PERSON_ADMIN_FLOW } from './person-admin.flow';
import { PersonFormFactory, type PersonFormModel } from './person-form';
import { ApiResponse } from '@mb/common';

export function createSavePersonAction() {
  const api = inject(ContactsApiService);
  const mutationActions = injectMutationActions();
  const tableState = inject(TableStateService<IPerson>);
  const flow = inject(PERSON_ADMIN_FLOW);

  const onError = (error: unknown) => {
    clientLogger.error('Error submitting person form', {
      error: error instanceof Error ? error : new Error(String(error)),
    });
  };

  async function onSaveSuccess(row: IPerson): Promise<void> {
    tableState.upsertRow(row);

    const selected = tableState.selected();
    if (selected && String(selected.id) === String(row.id)) {
      tableState.patchSelectedDetails({
        ...selected,
        ...row,
      });
    }

    tableState.refreshList();
    await flow.saved(row.id);
  }

  return async (model: PersonFormModel): Promise<void> => {
    if (model.id) {
      await mutationActions.updateWithToast({
        onError,
        mutate: (input) => api.updatePerson(input),
        input: PersonFormFactory.toUpdatePayload(model),
        onSuccess: async (result) => {
          if (!result.data) {
            return;
          }
          await onSaveSuccess(result.data);
        },
      });
      return;
    }

    await mutationActions.addWithToast({
      onError,
      mutate: (input) => api.createPerson(input),
      input: PersonFormFactory.toCreatePayload(model),
      onSuccess: async (result) => {
        if (!result.data) {
          return;
        }
        await onSaveSuccess(result.data);
      },
    });
  };
}

export function createDeletePersonAction() {
  const mutationActions = injectMutationActions();
  const api = inject(ContactsApiService);
  const tableState = inject(TableStateService<IPerson>);
  const flow = inject(PERSON_ADMIN_FLOW);

  return async (id: string): Promise<void> => {
    await mutationActions.confirmAndDelete({
      mutate: (personId) => api.deletePerson(personId),
      input: id,
      onSuccess: async () => {
        tableState.removeRow(id);
        tableState.refreshList();
        await tableState.selectById(null);
        await flow.deleted(id);
      },
    });
  };
}

export function createResendPersonInviteAction() {
  const api = inject(ContactsApiService);
  const scopedAccess = inject(SCOPED_ACCESS_GATE);
  const mutationActions = injectMutationActions();

  return async (
    personId: string,
    email: string,
    organizationId?: string | null,
  ): Promise<string | null> => {
    const orgId = organizationId ?? scopedAccess.selectedOrganizationId();
    if (!orgId) {
      return null;
    }

    const result = await mutationActions.mutateWithToast({
      mutate: (id) => api.resendPersonInvite(orgId, id),
      input: personId,
      successMessageKey: 'persons.list.invite.sent',
      successMessageParams: { email },
      onError: (error) => {
        clientLogger.error('Failed to resend person invite', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      },
    });

    return result?.data?.inviteUrl ?? null;
  };
}

export function createUnlinkPersonFromUserAction() {
  const mutationActions = injectMutationActions();
  const api = inject(ContactsApiService);
  const tableState = inject(TableStateService<IPerson>);

  return async (personId: string): Promise<void> => {
    await mutationActions.confirmAndMutate<string, ApiResponse<IPerson>>({
      titleKey: 'persons.list.unlink.confirmTitle',
      descriptionKey: 'persons.list.unlink.confirmDescription',
      actionLabelKey: 'persons.list.unlink.action',
      actionVariant: 'destructive',
      mutate: (id) => api.unlinkPersonFromUser(id),
      input: personId,
      successMessageKey: 'persons.list.unlink.success',
      onSuccess: async (result) => {
        if (!result.data) {
          return;
        }

        tableState.upsertRow(result.data);

        const selected = tableState.selected();
        if (selected && String(selected.id) === String(result.data.id)) {
          tableState.patchSelectedDetails({
            ...selected,
            ...result.data,
          });
        }

        tableState.refreshList();
      },
      onError: (error) => {
        clientLogger.error('Failed to unlink person from user account', {
          error: error instanceof Error ? error : new Error(String(error)),
        });
      },
    });
  };
}
